const mq = (query: string) => (typeof window !== 'undefined' ? window.matchMedia(query) : null)

export const motionQuery = mq('(prefers-reduced-motion: reduce)')
export const coarsePointer = mq('(pointer: coarse)')?.matches ?? false
export const finePointer = mq('(hover: hover) and (pointer: fine)')?.matches ?? false

/**
 * Reduced-motion policy for this site: remove *large* movement (screen shake, camera swings,
 * parallax, smooth-scroll hijacking, sliding reveals) but keep text typing, fades,
 * glitch flicker and slow ambient motion. Windows reports this when "Animation effects" is off.
 */
export const prefersReducedMotion = (): boolean => motionQuery?.matches ?? false

/** Rough "this device should take it easy" signal for the WebGL scene. */
export function isLowPowerDevice(): boolean {
  const nav = navigator as Navigator & { deviceMemory?: number }
  const cores = nav.hardwareConcurrency ?? 8
  const memory = nav.deviceMemory ?? 8
  return coarsePointer || cores <= 4 || memory <= 4
}

/**
 * Detect search crawlers (Googlebot, Bingbot, Yandex), AI agents, or headless environments
 * to bypass artificial preloader delays and ensure fast first paint for indexing bots.
 */
export function isBotOrHeadless(): boolean {
  if (typeof navigator === 'undefined') return true
  const ua = navigator.userAgent || ''
  if (/bot|crawler|spider|robot|crawling|googlebot|bingbot|baiduspider|yandex|duckduckbot|slurp|lighthouse|headlesschrome|headless|chrome-lighthouse/i.test(ua)) return true
  if (typeof document !== 'undefined' && document.documentElement.classList.contains('is-bot')) return true
  return false
}

function getStorage(storage: 'local' | 'session'): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return storage === 'local' ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

export function safeGet(storage: 'local' | 'session', key: string): string | null {
  try {
    const s = getStorage(storage)
    return s ? s.getItem(key) : null
  } catch {
    return null
  }
}

export function safeSet(storage: 'local' | 'session', key: string, value: string): void {
  try {
    const s = getStorage(storage)
    if (s) s.setItem(key, value)
  } catch {
    /* storage unavailable (private mode, blocked cookies) — preferences just won't persist */
  }
}

export function safeRemove(storage: 'local' | 'session', key: string): void {
  try {
    const s = getStorage(storage)
    if (s) s.removeItem(key)
  } catch {
    /* storage unavailable */
  }
}
